import { Download, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useWorkOrderCost } from '@/features/cost/queries';
import {
  costService,
  triggerDownload,
} from '@/features/cost/service';
import { formatMoney } from '@/features/cost/types';
import { useAuthStore } from '@/auth/store';

const PRIVILEGED_ROLES = ['superadmin', 'isi_admin', 'fleet_manager'] as const;

/**
 * L8 — WO cost card embedded in WorkOrderDetailPage.
 *
 * Visible only to roles that can read costs (superadmin, isi_admin,
 * fleet_manager). Hidden entirely for technicians/chiefs to keep their
 * UI lean — the backend would forbid them anyway.
 */
export function WorkOrderCostCard({ workOrderId }: { workOrderId: string }) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const canSee =
    user?.roles?.some((r) =>
      (PRIVILEGED_ROLES as readonly string[]).includes(r),
    ) ?? false;

  const { data, isLoading, isError } = useWorkOrderCost(
    canSee ? workOrderId : undefined,
  );

  const [downloading, setDownloading] = useState(false);

  if (!canSee) return null;

  const onDownload = async () => {
    setDownloading(true);
    try {
      const blob = await costService.downloadWorkOrderXlsx(workOrderId);
      const filename = data
        ? `wo-cost-${data.number}.xlsx`
        : `wo-cost-${workOrderId}.xlsx`;
      triggerDownload(blob, filename);
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (cents: number | null | undefined, currency = 'EUR') =>
    formatMoney(cents, currency, i18n.language);

  return (
    <Card data-testid="wo-cost-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet aria-hidden className="h-4 w-4" />
          {t('cost.cardTitle')}
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={onDownload}
          disabled={downloading || isLoading || isError}
          data-testid="wo-cost-download"
        >
          <Download aria-hidden className="mr-1 h-4 w-4" />
          {t('cost.export.excel')}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading || !data ? (
          <Skeleton className="h-24 w-full" />
        ) : isError ? (
          <p className="text-sm text-destructive">{t('cost.loadError')}</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat
                label={t('cost.parts.label')}
                value={fmt(data.parts.totalCents, data.currency)}
                hint={
                  data.parts.uncountedLines > 0
                    ? t('cost.parts.partial', {
                        counted: data.parts.countedLines,
                        total:
                          data.parts.countedLines + data.parts.uncountedLines,
                      })
                    : null
                }
              />
              <Stat
                label={t('cost.labor.label')}
                value={fmt(data.labor.totalCents, data.currency)}
                hint={
                  data.labor.actualHours !== null &&
                  data.labor.averageHourlyRateCents !== null
                    ? t('cost.labor.breakdown', {
                        hours: data.labor.actualHours,
                        rate: fmt(
                          data.labor.averageHourlyRateCents,
                          data.currency,
                        ),
                      })
                    : t('cost.labor.empty')
                }
              />
              <Stat
                label={t('cost.total.label')}
                value={fmt(data.totalCents, data.currency)}
                hint={t('cost.total.hint', { currency: data.currency })}
                emphasis
              />
            </div>

            {data.parts.lines.length > 0 && (
              <details className="rounded-md border bg-muted/30 p-3">
                <summary className="cursor-pointer text-sm font-medium">
                  {t('cost.parts.breakdown', {
                    count: data.parts.lines.length,
                  })}
                </summary>
                <table className="mt-2 w-full text-sm" data-testid="wo-cost-parts-table">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="py-1">{t('cost.parts.col.code')}</th>
                      <th className="py-1">{t('cost.parts.col.name')}</th>
                      <th className="py-1 text-right">{t('cost.parts.col.qty')}</th>
                      <th className="py-1 text-right">{t('cost.parts.col.unit')}</th>
                      <th className="py-1 text-right">{t('cost.parts.col.lineTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.parts.lines.map((l) => (
                      <tr key={l.workOrderPartId} className="border-t">
                        <td className="py-1 font-mono text-xs">{l.partCode}</td>
                        <td className="py-1">{l.partName}</td>
                        <td className="py-1 text-right">{l.quantity}</td>
                        <td className="py-1 text-right">
                          {fmt(l.unitCostCents, data.currency)}
                        </td>
                        <td className="py-1 text-right font-medium">
                          {fmt(l.lineTotalCents, data.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: string;
  hint: string | null;
  emphasis?: boolean;
}) {
  return (
    <div className={emphasis ? 'rounded-md border bg-primary/5 p-3' : 'p-3'}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={emphasis ? 'text-2xl font-semibold' : 'text-xl font-semibold'}>
        {value}
      </p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
